import { getByPath } from '../interpolation/getByPath';
import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import type { PathSegment } from '../interpolation/types';
import { warnOnce } from '../warnOnce';

/**
 * Returned from a single-expression `{{scope.<key>}}` when no ancestor
 * published that key. A published `null` is a real value and is not
 * this sentinel.
 */
export interface UnpublishedScopeSentinel {
  readonly kind: 'unpublished-scope';
  readonly key: string;
}

/** Distinguishes a scope-key path this helper does not own. */
export const SCOPE_PATH_PASSTHROUGH = Symbol('scope-path-passthrough');

/**
 * Set on an interpolation context only when `scope` came from
 * `PublishedScopeProvider`. A data source named `scope` must not
 * receive unpublished-key warnings.
 */
const PUBLISHED_SCOPE = Symbol('published-scope');

/** Mark `context` so `{{scope.*}}` uses unpublished-key semantics. */
export function markPublishedScopeContext(
  context: Record<string, unknown>,
): void {
  Object.defineProperty(context, PUBLISHED_SCOPE, {
    value: true,
    enumerable: false,
  });
}

export function isPublishedScopeContext(context: unknown): boolean {
  return (
    typeof context === 'object' &&
    context !== null &&
    (context as Record<symbol, unknown>)[PUBLISHED_SCOPE] === true
  );
}

const UNPUBLISHED_SCOPE = Symbol('unpublished-scope');

export function isUnpublishedScope(
  value: unknown,
): value is UnpublishedScopeSentinel {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<symbol, unknown>)[UNPUBLISHED_SCOPE] === true
  );
}

/**
 * Resolve `{{scope.<key>}}` (and a deeper path under a published key).
 *
 * A missing key warns once and returns the sentinel. Callers that
 * stringify mixed templates turn that sentinel into
 * `[unpublished scope: <key>]` so a URL cannot embed the object.
 * Paths that are not `scope.<key>` return {@link SCOPE_PATH_PASSTHROUGH}
 * and the ordinary walker continues.
 */
export function readPublishedScopePath(
  context: unknown,
  segments: ReadonlyArray<PathSegment>,
): unknown {
  const head = segments[0];
  const keySegment = segments[1];
  if (
    head?.type !== 'key' ||
    head.name !== 'scope' ||
    keySegment?.type !== 'key' ||
    FORBIDDEN_KEYS.has(keySegment.name)
  ) {
    return SCOPE_PATH_PASSTHROUGH;
  }

  if (!isPublishedScopeContext(context)) {
    return SCOPE_PATH_PASSTHROUGH;
  }

  const bag = scopeBag(context);
  if (bag && Object.prototype.hasOwnProperty.call(bag, keySegment.name)) {
    const value = bag[keySegment.name];
    if (segments.length === 2) {
      return value;
    }
    return getByPath(value, segments.slice(2));
  }

  warnOnce(
    'published-scope',
    `[published-scope] Unpublished scope key "${keySegment.name}".`,
  );
  return unpublishedScopeSentinel(keySegment.name);
}

function unpublishedScopeSentinel(key: string): UnpublishedScopeSentinel {
  const label = `[unpublished scope: ${key}]`;
  const sentinel: UnpublishedScopeSentinel = {
    kind: 'unpublished-scope',
    key,
  };
  Object.defineProperty(sentinel, UNPUBLISHED_SCOPE, {
    value: true,
    enumerable: false,
  });
  Object.defineProperty(sentinel, 'toString', {
    value: () => label,
    enumerable: false,
  });
  Object.defineProperty(sentinel, 'toJSON', {
    value: () => label,
    enumerable: false,
  });
  return sentinel;
}

function scopeBag(context: unknown): Record<string, unknown> | undefined {
  if (context === null || typeof context !== 'object') {
    return undefined;
  }
  if (!Object.prototype.hasOwnProperty.call(context, 'scope')) {
    return undefined;
  }
  const scope = (context as Record<string, unknown>).scope;
  if (scope === null || typeof scope !== 'object' || Array.isArray(scope)) {
    return undefined;
  }
  return scope as Record<string, unknown>;
}
